import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  InputBase,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { useMemo, useState, useEffect, useContext } from "react";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useDeals } from "../../hooks/useDealList";
import { useFollowingDeals } from "../../hooks/useFollowingDeals";
import { useAuth } from "../../contexts/AuthContext";
import CustomButton from "../ui/CustomButton";
import {
  DealListRow,
  DealListRowSkeleton,
} from "./dealList/components/DealListRow";
import DealListLoadMoreButton from "./dealList/components/DealListLoadMoreButton";
import FilterEmptyHint from "./dealList/components/FilterEmptyHint";
import DealsFilterIconButton from "./dealList/components/DealsFilterIconButton";
import {
  DEAL_LIST_BOTTOM_SCROLL_RESERVE,
  DEAL_LIST_SECTION_PAGE_SIZE,
} from "./dealList/constants";
import {
  groupDealsByExpiryBucket,
  sortDealsByValidityPriority,
} from "./dealList/utils/calendarDealBuckets";
import { uiNeutrals } from "../../constants/uiNeutrals";
import { countAppliedDealsFilters } from "../../utils/countAppliedDealsFilters";
import { TabsContext } from "./dealsTabsContext";

const SECTIONS = [
  { key: "today", label: "EXPIRING TODAY" },
  { key: "tomorrow", label: "EXPIRING TOMORROW" },
  { key: "thisWeek", label: "EXPIRING THIS WEEK" },
  { key: "later", label: "EXPIRING LATER" },
];

const EMPTY_FOLLOWING_FILTERS = {
  timeOfDay: "",
  date: "",
  area: "",
  category: "",
  discountType: "",
  priceBand: "",
};

/** Same shell as `BrowseRestaurantsPanel` search row (height, padding, margin, trailing slot). */
const FollowingCalendarSearchBar = ({
  searchInput,
  onSearchChange,
  onClearSearch,
  surfaceNeutral,
  filterButton = null,
  placeholder = "Search deals",
  inputAriaLabel = "Search deals in calendar",
}) => {
  const showClear = Boolean(searchInput.trim());
  const trailingIconSx = {
    flexShrink: 0,
    p: 0.35,
    color: "#888",
    WebkitTapHighlightColor: "transparent",
  };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 0.85,
        px: 1.35,
        mb: 1,
        borderRadius: 2,
        bgcolor: surfaceNeutral.surface,
        border: "none",
        boxShadow: "none",
      }}
    >
      <SearchIcon sx={{ color: "#888", fontSize: 20, flexShrink: 0 }} />
      <InputBase
        placeholder={placeholder}
        value={searchInput}
        onChange={onSearchChange}
        autoFocus={false}
        inputProps={{
          inputMode: "search",
          autoCapitalize: "none",
          autoCorrect: "off",
          spellCheck: "false",
          "aria-label": inputAriaLabel,
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          "& .MuiInputBase-input": {
            fontSize: "16px",
            lineHeight: 1.25,
          },
        }}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          gap: 0.25,
          ml: "auto",
        }}
      >
        <IconButton
          type="button"
          size="small"
          onClick={onClearSearch}
          aria-label={showClear ? "Clear search" : undefined}
          aria-hidden={!showClear}
          tabIndex={showClear ? 0 : -1}
          sx={{
            ...trailingIconSx,
            display: showClear ? "inline-flex" : "none",
          }}
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
        {filterButton}
      </Box>
    </Box>
  );
};

const listShellSx = {
  maxWidth: 720,
  mx: "auto",
  px: { xs: 2 },
  pt: { xs: 0.5, md: 0 },
  pb: DEAL_LIST_BOTTOM_SCROLL_RESERVE,
};

const INITIAL_SECTION_LIMITS = {
  today: DEAL_LIST_SECTION_PAGE_SIZE,
  tomorrow: DEAL_LIST_SECTION_PAGE_SIZE,
  thisWeek: DEAL_LIST_SECTION_PAGE_SIZE,
  later: DEAL_LIST_SECTION_PAGE_SIZE,
};

const DealsCalendarTab = ({
  isActive,
  onExpandDeal,
  followingOnly = false,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const n = theme.palette.neutral || uiNeutrals;
  const tabs = useContext(TabsContext);
  const followingFromContext =
    tabs?.followingFilters ?? EMPTY_FOLLOWING_FILTERS;
  const openFilters = tabs?.setOpenFilterDrawer;

  const [searchInput, setSearchInput] = useState("");
  const [sectionLimits, setSectionLimits] = useState({
    ...INITIAL_SECTION_LIMITS,
  });
  const [flatListLimit, setFlatListLimit] = useState(
    DEAL_LIST_SECTION_PAGE_SIZE,
  );

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    setSearchInput(p.get("search") || "");
  }, [location.search]);

  useEffect(() => {
    if (!isActive) return;
    const timer = setTimeout(() => {
      const p = new URLSearchParams(location.search);
      const next = searchInput.trim();
      const cur = (p.get("search") || "").trim();
      if (next === cur) return;
      if (next) p.set("search", next);
      else p.delete("search");
      navigate(`${location.pathname}?${p.toString()}`, { replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, isActive, location.pathname, location.search, navigate]);

  const filters = useMemo(() => {
    const p = new URLSearchParams(location.search);
    if (followingOnly) {
      return {
        area: p.get("area") || "",
        date: p.get("date") || "",
        timeOfDay: p.get("time_of_day") || "",
        category: p.get("category") || "",
        discountType: p.get("discount_type") || "",
        priceBand: p.get("price_band") || "",
        search: p.get("search") || "",
      };
    }
    const pick = (paramKey, ctxVal) =>
      p.has(paramKey) ? p.get(paramKey) || "" : ctxVal || "";
    return {
      area: pick("area", followingFromContext.area),
      date: pick("date", followingFromContext.date),
      timeOfDay: pick("time_of_day", followingFromContext.timeOfDay),
      category: pick("category", followingFromContext.category),
      discountType: pick("discount_type", followingFromContext.discountType),
      priceBand: pick("price_band", followingFromContext.priceBand),
      search: p.get("search") || "",
    };
  }, [location.search, followingFromContext, followingOnly]);

  const appliedFilterCount = useMemo(
    () => countAppliedDealsFilters(filters),
    [filters],
  );

  const filterButton = openFilters ? (
    <DealsFilterIconButton
      count={appliedFilterCount}
      onClick={() => openFilters(true)}
    />
  ) : null;

  const trimmedSearch = (filters.search || "").trim();
  const isSearchResultsMode = trimmedSearch.length > 0;

  /** With text search, query all active deals matching `search` (ignore sheet filters). */
  const dealsQueryFilters = useMemo(() => {
    if (!trimmedSearch) return filters;
    return {
      ...filters,
      area: "",
      date: "",
      timeOfDay: "",
      category: "",
      discountType: "",
      priceBand: "",
      search: trimmedSearch,
    };
  }, [filters, trimmedSearch]);

  const dealsQueryKey = useMemo(
    () => JSON.stringify(dealsQueryFilters),
    [dealsQueryFilters],
  );

  useEffect(() => {
    setSectionLimits({ ...INITIAL_SECTION_LIMITS });
    setFlatListLimit(DEAL_LIST_SECTION_PAGE_SIZE);
  }, [dealsQueryKey]);

  const handleClearSearch = () => {
    setSearchInput("");
    const p = new URLSearchParams(location.search);
    p.delete("search");
    const qs = p.toString();
    navigate(qs ? `${location.pathname}?${qs}` : location.pathname, {
      replace: true,
    });
  };

  const browseDeals = useDeals(dealsQueryFilters, isMobile, {
    enabled: isActive && !followingOnly,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const followingDeals = useFollowingDeals(dealsQueryFilters, isMobile, {
    enabled: isActive && followingOnly,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const {
    deals,
    loading,
    status,
    loadingMore,
    hasMore,
    nextCursor,
    fetchDeals,
  } = followingOnly ? followingDeals : browseDeals;

  const sortedDeals = useMemo(
    () => sortDealsByValidityPriority(deals),
    [deals],
  );

  const buckets = useMemo(
    () => groupDealsByExpiryBucket(sortedDeals),
    [sortedDeals],
  );

  const handleBucketSectionLoadMore = (key) => {
    const list = buckets[key];
    const lim = sectionLimits[key];
    if (lim < list.length) {
      setSectionLimits((prev) => ({
        ...prev,
        [key]: prev[key] + DEAL_LIST_SECTION_PAGE_SIZE,
      }));
      return;
    }
    if (hasMore && !loadingMore) {
      fetchDeals(nextCursor, true);
    }
  };

  const handleFlatListLoadMore = () => {
    if (flatListLimit < deals.length) {
      setFlatListLimit((n) => n + DEAL_LIST_SECTION_PAGE_SIZE);
      return;
    }
    if (hasMore && !loadingMore) {
      fetchDeals(nextCursor, true);
    }
  };

  const handleCalendarFetchMore = () => {
    if (hasMore && !loadingMore) fetchDeals(nextCursor, true);
  };

  const totalInBuckets =
    buckets.today.length +
    buckets.tomorrow.length +
    buckets.thisWeek.length +
    buckets.later.length;

  const calendarNeedsApiPage =
    hasMore &&
    !SECTIONS.some(({ key }) => buckets[key].length > sectionLimits[key]);

  const searchBar = (
    <FollowingCalendarSearchBar
      searchInput={searchInput}
      onSearchChange={(e) => setSearchInput(e.target.value)}
      onClearSearch={handleClearSearch}
      surfaceNeutral={n}
      filterButton={followingOnly ? null : filterButton}
      placeholder={
        followingOnly ? "Search deals from brands you follow" : "Search deals"
      }
      inputAriaLabel={
        followingOnly
          ? "Search deals from brands you follow"
          : "Search deals in calendar"
      }
    />
  );

  if (followingOnly && !isAuthenticated) {
    return (
      <Box sx={listShellSx}>
        {searchBar}
        <Box sx={{ py: 6, textAlign: "center", px: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Sign in to see following
          </Typography>

          <CustomButton
            variant="secondary"
            onClick={() => navigate("/login")}
            sx={{ minWidth: 120, mt: 2 }}
          >
            Login
          </CustomButton>
        </Box>
      </Box>
    );
  }

  if (isActive && loading && !deals.length) {
    return (
      <Box sx={listShellSx}>
        {searchBar}
        {Array.from({ length: 8 }).map((_, i) => (
          <DealListRowSkeleton key={i} />
        ))}
      </Box>
    );
  }

  if (status === "error") {
    return (
      <Box sx={listShellSx}>
        {searchBar}
        <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>
          Could not load deals. Try again later.
        </Typography>
      </Box>
    );
  }

  if (isSearchResultsMode) {
    if (isActive && !loading && deals.length === 0) {
      return (
        <Box sx={listShellSx}>
          {searchBar}
          <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>
            No deals match your search.
          </Typography>
          <FilterEmptyHint
            preamble="Try another search phrase, or change your filters to see more results."
            onOpenFilters={openFilters ? () => openFilters(true) : undefined}
          />
        </Box>
      );
    }
    const visibleDeals = sortedDeals.slice(0, flatListLimit);
    const showFlatLoadMore =
      deals.length > 0 &&
      (deals.length > flatListLimit ||
        (hasMore && deals.length >= flatListLimit));

    return (
      <Box sx={listShellSx}>
        {searchBar}
        <Box sx={{ mt: 1 }}>
          {visibleDeals.map((deal, index) => (
            <DealListRow
              key={deal._id}
              deal={deal}
              index={index}
              onExpandDeal={onExpandDeal}
            />
          ))}
        </Box>
        {loadingMore && (
          <Box sx={{ mt: 1 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <DealListRowSkeleton key={`more-${i}`} />
            ))}
          </Box>
        )}
        {showFlatLoadMore ? (
          <DealListLoadMoreButton
            onClick={handleFlatListLoadMore}
            loading={loadingMore}
          />
        ) : null}
      </Box>
    );
  }

  if (isActive && !loading && deals.length > 0 && totalInBuckets === 0) {
    return (
      <Box sx={listShellSx}>
        {searchBar}
        <Typography sx={{ color: "#666", fontSize: "0.9rem", lineHeight: 1.5 }}>
          No deals with a clear end date in this list. Try widening your date
          filter or search.
        </Typography>
        <FilterEmptyHint
          preamble="Try different filter options—you can reopen the filter sheet here."
          onOpenFilters={openFilters ? () => openFilters(true) : undefined}
        />
      </Box>
    );
  }

  if (isActive && !loading && deals.length === 0) {
    return (
      <Box sx={listShellSx}>
        {searchBar}
        <Typography sx={{ color: "#666", fontSize: "0.9rem", mt: 2 }}>
          {followingOnly
            ? "No deals for restaurants you follow."
            : "No deals match your filters."}
        </Typography>
        {!followingOnly ? (
          <FilterEmptyHint
            preamble="Try changing your filters to see more deals."
            onOpenFilters={openFilters ? () => openFilters(true) : undefined}
          />
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={listShellSx}>
      {searchBar}
      <Box sx={{ mt: 4 }}>
        {SECTIONS.map(({ key, label }) => {
          const list = buckets[key];
          if (!list.length) return null;
          const lim = sectionLimits[key];
          const visible = list.slice(0, lim);
          const showSectionLoadMore = list.length > lim;
          return (
            <Box key={key} sx={{ mb: 0.5 }}>
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  color: "#888",
                  mt: key === "today" ? 0 : 2.25,
                  mb: 1,
                }}
              >
                {label}
              </Typography>
              <Box>
                {visible.map((deal, index) => (
                  <DealListRow
                    key={deal._id}
                    deal={deal}
                    index={index}
                    onExpandDeal={onExpandDeal}
                  />
                ))}
              </Box>
              {showSectionLoadMore ? (
                <DealListLoadMoreButton
                  onClick={() => handleBucketSectionLoadMore(key)}
                  loading={loadingMore}
                />
              ) : null}
            </Box>
          );
        })}
        {calendarNeedsApiPage ? (
          <DealListLoadMoreButton
            onClick={handleCalendarFetchMore}
            loading={loadingMore}
          />
        ) : null}
      </Box>
    </Box>
  );
};

export default DealsCalendarTab;
